/* eslint-disable */
import firstOrUndefined from '../../util/firstOrUndefined';
import logger from '../../log';
import { TransportType } from './data';
import { Record } from './TransferRecord';
import Node from './Node';
import {
  AttachmentRequestRecordImpl,
  ClusterCandidateRecordImpl,
  ClusterDetachRecordImpl,
  ConnectionRecordImpl,
  ContentNotificationRecordImpl,
  NetworkTupleImpl,
  NodeStateRecordImpl,
  PeerConnectionImpl,
  PeeringProtocolImpl,
  PeerNotificationRecordImpl,
  PeerRequestRecordImpl
} from './Records';
import EventEmitter from '../../common/EventEmitter';
import { InternalEvents } from '../../events';
import { PENDING_CLUSTER, PlayType } from '../../common/constants';
import ObjectCache from './ObjectCache';
import { SourceType } from './interfaces';
import {
  AttachmentRequestRecord,
  ConnectionRecord,
  KernelContentNotification,
  KernelDetachRecord,
  PeerNotification,
  KernelPeerRequest,
  PeerState,
  KernelTransferRecord
} from './kernelInterfaces';
import { Emitter } from '../../common/interfaces';

const log = logger.getLogger(__filename);

export function convertType(type: number | SourceType): SourceType {
  // The kernel is using enums with int values. So here we convert those values to their string form
  switch (type) {
    case 0:
      return SourceType.INGEST;
    case 1:
      return SourceType.GRID;
    case 2:
      return SourceType.PEER;
    case 3:
      return SourceType.PULL_THROUGH;
    case 4:
      return SourceType.CACHE_HIT;
    case 5:
      return SourceType.GRAPH;
    default:
      return type as SourceType;
  }
}

type WasmFunc = { (): void; delete?: () => void; opcall?: () => void };

export default class Metrics {
  node: Node;
  cache: ObjectCache;
  appName: string;
  playType: PlayType;
  shutdown: boolean;
  eventEmitter: Emitter;

  constructor(
    node: Node,
    cache: ObjectCache,
    appName: string,
    playType: PlayType,
    emitter: Emitter
  ) {
    this.node = node;
    this.cache = cache;
    this.appName = appName;
    this.playType = playType;
    this.shutdown = false;
    this.eventEmitter = emitter || EventEmitter.getInstance();
  }

  stop(): void {
    this.shutdown = true;
  }

  SetTimer(numMilliseconds: number, func: WasmFunc) {
    log.trace('setting timer for', numMilliseconds);
    setTimeout(
      function (isShutdown: boolean): void {
        if (isShutdown) {
          if (func.delete) {
            func.delete();
          }
          return;
        }
        try {
          if (func.opcall) {
            // This callback came from the wasm kernel
            func.opcall();
            func.delete && func.delete();
          } else {
            func();
          }
        } catch (e) {
          log.trace(
            'could not execute function during SetTimer',
            e,
            func,
            numMilliseconds
          );
        }
      },
      numMilliseconds,
      this.shutdown
    );
  }

  addTransferRecord({
    node,
    sourcePeer,
    objectId,
    type,
    start,
    load,
    end,
    statusCode
  }: KernelTransferRecord): Promise<number> {
    const convertedType = convertType(type);
    // INGEST Records are from the agent, we don't add records for them.
    if (convertedType === 'INGEST') {
      return Promise.resolve(500);
    }
    let clusterId = PENDING_CLUSTER;
    const cluster = (node.attachment && node.attachment.activeCluster) || {
      id: undefined
    };
    if (cluster.id && cluster.id !== '') {
      clusterId = cluster.id;
    }
    const sourceId = sourcePeer === this.node.nodeId ? objectId : sourcePeer;
    const ts = Date.now();
    return this.cache.getPayloadSize(objectId).then(size => {
      const record = new Record(
        convertedType,
        this.node.nodeId,
        this.node.tenantId,
        sourceId,
        this.node.contentId,
        objectId,
        ts,
        start,
        this.appName,
        this.playType,
        clusterId,
        true
      );
      record.setNetworkData(Node.from(this.node));
      record.startLoading(load);
      if (statusCode !== 200) {
        record.fail(end, sourceId, statusCode);
      } else {
        record.stop(end, sourceId, size, statusCode);
      }
      try {
        this.eventEmitter.emit(InternalEvents.TRANSFER_RECORD, {
          data: record
        });
        return Promise.resolve(200);
      } catch (e) {
        log.warn('Failed sending transfer record', e);
        return Promise.resolve(500);
      }
    });
  }

  addContentNotificationRecord({
    node,
    reportingPeer,
    objectId,
    objectState,
    wallReceivedTime,
    monotonicReceivedTime
  }: KernelContentNotification) {
    const clusterId =
      (node.attachment &&
        node.attachment.activeCluster &&
        node.attachment.activeCluster.id) ||
      '';
    const record = new ContentNotificationRecordImpl(
      node.id,
      node.agentId,
      node.contentId,
      node.tenantId,
      clusterId,
      node.locality,
      wallReceivedTime,
      monotonicReceivedTime,
      reportingPeer,
      objectId,
      objectState
    );
    try {
      // TODO - Content notifications are getting noisy and we don't yet need to ship them.
      // this.eventEmitter.emit(InternalEvents.CONTENT_NOTIFICATION, {data:record});
      return 200;
    } catch (e) {
      log.warn('Failed sending content notification record', e);
      return 404;
    }
  }
  // TODO: Replace reporter with ID of reported peer, not the reporter. Reporter is by default the
  // node adding the record.
  addPeerStateRecord({
    node,
    peer,
    wallSentTime,
    monotonicSentTime
  }: PeerState) {
    const clusterId =
      (node.attachment &&
        node.attachment.activeCluster &&
        node.attachment.activeCluster.id) ||
      '';
    const record = new NodeStateRecordImpl(
      node.id,
      node.agentId,
      node.contentId,
      node.tenantId,
      clusterId,
      node.locality,
      wallSentTime,
      monotonicSentTime,
      peer.id,
      peer.attachment && peer.attachment.depth,
      peer.state
    );
    try {
      this.eventEmitter.emit(InternalEvents.NODE_STATE, { data: record });
      return 200;
    } catch (e) {
      log.warn('Failed sending node state record', e);
      return 404;
    }
  }
  addPeerNotificationRecord({
    node,
    reportingPeer,
    reportedCandidate,
    wallReceivedTime,
    monotonicReceivedTime
  }: PeerNotification) {
    const clusterId =
      (node.attachment &&
        node.attachment.activeCluster &&
        node.attachment.activeCluster.id) ||
      '';
    const record = new PeerNotificationRecordImpl(
      node.id,
      node.agentId,
      node.contentId,
      node.tenantId,
      clusterId,
      node.locality,
      wallReceivedTime,
      monotonicReceivedTime,
      reportingPeer,
      reportedCandidate.nodeId,
      node.state
    );
    try {
      this.eventEmitter.emit(InternalEvents.PEER_NOTIFICATION, {
        data: record
      });
      return 200;
    } catch (e) {
      log.warn('Failed sending peer notification record', e);
      return 404;
    }
  }
  addPeerRequestRecord({
    node,
    respondingPeer,
    origin,
    wallRequestTime,
    monotonicRequestStart,
    monotonicRequestEnd,
    candidates,
    path,
    statusCode
  }: KernelPeerRequest) {
    const clusterId =
      (node.attachment &&
        node.attachment.activeCluster &&
        node.attachment.activeCluster.id) ||
      '';
    const record = new PeerRequestRecordImpl(
      node.id,
      node.agentId,
      node.contentId,
      node.tenantId,
      clusterId,
      node.locality,
      respondingPeer,
      origin,
      wallRequestTime,
      monotonicRequestStart,
      monotonicRequestEnd,
      candidates.map(c => c.nodeId),
      path,
      statusCode
    );
    try {
      this.eventEmitter.emit(InternalEvents.PEER_REQUEST, { data: record });
      return 200;
    } catch (e) {
      log.warn('Failed sending peer request record', e);
      return 404;
    }
  }

  addAttachmentRequestRecord({
    node,
    walltimeRequestStart,
    monotonicRequestStart,
    monotonicResponseEnd,
    options,
    backupCluster,
    statusCode
  }: AttachmentRequestRecord) {
    const candidates = options.map(cc => {
      return new ClusterCandidateRecordImpl(
        cc.cluster.id,
        cc.peers.map(p => p.nodeId)
      );
    });
    const clusterId =
      (node.attachment &&
        node.attachment.activeCluster &&
        node.attachment.activeCluster.id) ||
      '';
    const record = new AttachmentRequestRecordImpl(
      node.id,
      node.agentId,
      node.contentId,
      node.tenantId,
      clusterId,
      node.locality,
      walltimeRequestStart,
      monotonicRequestStart,
      monotonicResponseEnd,
      candidates,
      backupCluster,
      statusCode
    );
    try {
      this.eventEmitter.emit(InternalEvents.ATTACHMENT_REQUEST, {
        data: record
      });
      return 200;
    } catch (e) {
      log.warn('Failed sending attachment request record', e);
      return 404;
    }
  }

  addClusterDetachRecord({
    node,
    sendingPeer,
    detachingPeer,
    wallReceivedTime,
    monotonicReceivedTime
  }: KernelDetachRecord) {
    const clusterId =
      (node.attachment &&
        node.attachment.activeCluster &&
        node.attachment.activeCluster.id) ||
      '';
    const record = new ClusterDetachRecordImpl(
      node.id,
      node.agentId,
      node.contentId,
      node.tenantId,
      clusterId,
      node.locality,
      wallReceivedTime,
      monotonicReceivedTime,
      sendingPeer,
      detachingPeer
    );
    try {
      this.eventEmitter.emit(InternalEvents.DETACH, { data: record });
      return 200;
    } catch (e) {
      log.warn('Failed sending detach record', e);
      return 404;
    }
  }

  addConnectionRecord({
    node,
    peer,
    type,
    status,
    state,
    outbound,
    monotonic,
    wallClock
  }: ConnectionRecord) {
    const attachment = node.attachment;
    const clusterId =
      (attachment && attachment.activeCluster && attachment.activeCluster.id) ||
      '';
    const nodeDepth = (attachment && attachment.depth) || 0;
    const peerDepth = peer.depth;
    const peerId = peer.nodeId;
    let nodeCapacity = 0;
    let peerCapacity = 0;
    switch (type) {
      case 'UPSTREAM':
        nodeCapacity = node.state.upCapacity;
        peerCapacity = peer.state.downCapacity;
        break;
      case 'MIDSTREAM':
        nodeCapacity = node.state.midCapacity;
        peerCapacity = peer.state.midCapacity;
        break;
      case 'DOWNSTREAM':
        nodeCapacity = node.state.downCapacity;
        peerCapacity = peer.state.upCapacity;
        break;
    }
    const { externalIps, internalIps, internalMdns } = Node.from(this.node);
    const nodeTuple = new NetworkTupleImpl(
      node.id,
      firstOrUndefined(externalIps),
      firstOrUndefined(internalIps),
      0,
      nodeDepth,
      nodeCapacity,
      internalMdns
    );
    const peerTuple = new NetworkTupleImpl(
      peerId,
      '',
      '',
      0,
      peerDepth,
      peerCapacity,
      ''
    );
    const peeringProtocol = new PeeringProtocolImpl(
      TransportType.WEBRTC,
      peer.protocol.majorVersion,
      peer.protocol.minorVersion,
      peer.protocol.uris
    );
    const peerConn = new PeerConnectionImpl(
      type,
      state,
      outbound ? nodeTuple : peerTuple,
      outbound ? peerTuple : nodeTuple,
      peeringProtocol,
      status
    );
    const record = new ConnectionRecordImpl(
      node.id,
      node.agentId,
      node.contentId,
      node.tenantId,
      clusterId,
      node.locality,
      wallClock,
      monotonic,
      peerConn
    );
    try {
      this.eventEmitter.emit(InternalEvents.CONNECTION_RECORD, {
        data: record
      });
      return 200;
    } catch (e) {
      log.warn('Failed sending connection record', e);
      return 404;
    }
  }
}
